import { Delete } from "lucide-react";
import { cn } from "@/lib/utils";
import { OPERATOR_SYMBOL, type CalcKey, type PadKey } from "./amountPad";

interface NumberPadProps {
  /** Fires once per tap with the key pressed. State lives in the parent. */
  onKey: (key: CalcKey) => void;
  /**
   * `calculator` adds the arithmetic column (÷ × − +), an `=` and a `C`, for
   * the full-screen mobile entry screen. Omitted, the pad is the original
   * 12-key digit pad, unchanged — that is what the ≥sm dialog still renders.
   */
  variant?: "digits" | "calculator";
  className?: string;
}

/**
 * Digits in phone order with a fourth utility column, so the pad is three
 * rows tall instead of four — 144px rather than 194px. On a 390px sheet each
 * key is still ~87px wide, so nothing is cramped by the extra column, and the
 * 50px saved is 50px more of the form visible above it.
 */
const DIGIT_ROWS: PadKey[][] = [
  ["1", "2", "3", "backspace"],
  ["4", "5", "6", "decimal"],
  ["7", "8", "9", "0"],
];

/**
 * Calculator layout, matching the reference: three digit columns with the
 * arithmetic column down the right-hand side. Digits run 7-8-9 at the top
 * (calculator order, not phone order) because the operator column makes this
 * read as a calculator, and a calculator's 7 is top-left.
 */
const CALC_ROWS: CalcKey[][] = [
  ["7", "8", "9", "divide"],
  ["4", "5", "6", "multiply"],
  ["1", "2", "3", "subtract"],
  ["decimal", "0", "backspace", "add"],
  ["clear", "equals"],
];

const KEY_LABEL: Partial<Record<CalcKey, string>> = {
  decimal: "Decimal point",
  backspace: "Delete last digit",
  clear: "Clear",
  equals: "Equals",
  add: "Add",
  subtract: "Subtract",
  multiply: "Multiply",
  divide: "Divide",
};

function KeyFace({ keyName }: { keyName: CalcKey }) {
  if (keyName === "backspace") return <Delete className="h-5 w-5" aria-hidden />;
  if (keyName === "decimal") return <>.</>;
  if (keyName === "clear") return <>C</>;
  if (keyName === "equals") return <>=</>;
  if (keyName === "add" || keyName === "subtract" || keyName === "multiply" || keyName === "divide") {
    return <>{OPERATOR_SYMBOL[keyName]}</>;
  }
  return <>{keyName}</>;
}

/**
 * In-sheet keypad for the amount field.
 *
 * Exists so the OS keyboard never opens on mobile: the keyboard covers the
 * bottom half of the screen, which is where the category and account controls
 * live, and the only way to reach them was to dismiss it first. The pad sits
 * inside the entry surface, so nothing is ever occluded.
 *
 * Keys are at least 44px tall — this is the primary one-thumb control, so it
 * sits above the app-wide 40px touch floor rather than at it. Every key
 * carries an accessible name; backspace and the operators are distinguishable
 * by name, not only by glyph.
 *
 * Presses do not move focus (`onMouseDown` is prevented), so the amount field
 * keeps focus and a physical keyboard keeps working alongside the pad.
 */
export function NumberPad({ onKey, variant = "digits", className }: NumberPadProps) {
  const calculator = variant === "calculator";
  const keys = (calculator ? CALC_ROWS : DIGIT_ROWS).flat();

  return (
    <div
      role="group"
      aria-label="Amount keypad"
      className={cn(
        "grid grid-cols-4 gap-1.5",
        // Five equal rows that stretch to the height the parent gives them,
        // so the keys absorb whatever the amount hero does not need.
        calculator && "grid-rows-5",
        className,
      )}
    >
      {keys.map((key) => {
        const isBackspace = key === "backspace";
        const isOperator =
          key === "add" || key === "subtract" || key === "multiply" || key === "divide";
        const isEquals = key === "equals";
        const isClear = key === "clear";
        return (
          <button
            key={key}
            type="button"
            aria-label={KEY_LABEL[key] ?? key}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onKey(key)}
            className={cn(
              "flex items-center justify-center rounded-[9px] border font-semibold tabular-nums transition-colors active:scale-[0.97]",
              calculator ? "h-full min-h-12 text-xl" : "h-11 text-lg",
              isClear && "col-span-3",
              isEquals
                ? "border-transparent bg-[var(--cp-accent)] text-white"
                : isOperator
                  ? "border-[var(--cp-line)] bg-[var(--cp-surface-2)] text-[var(--cp-accent-ink)]"
                  : isBackspace || isClear
                    ? "border-[var(--cp-line)] bg-[var(--cp-surface-2)] text-[var(--cp-ink-2)] hover:bg-[var(--cp-line)]"
                    : "border-[var(--cp-line)] bg-[var(--cp-surface)] text-[var(--cp-ink)] hover:bg-[var(--cp-surface-2)]",
            )}
          >
            <KeyFace keyName={key} />
          </button>
        );
      })}
    </div>
  );
}
